import {
  INVENTORY_REASONS,
  INVENTORY_STATUSES,
  type InventoryDetail,
  type InventoryReason,
  TOOL_STATES,
} from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus, QrCode } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import {
  CHECK_TONE,
  INVENTORY_TONE,
  inventoriesQuery,
  inventoryLastQuery,
  TOOL_STATE_TONE,
  toolsQuery,
  warehousesQuery,
} from './queries';
import { ToolUnitForm } from './ToolPage';

/* ───────────────────────── Инвентаризация ───────────────────────── */

export function InventoriesTab() {
  const navigate = useNavigate();
  const warehouses = useQuery(warehousesQuery);
  const last = useQuery(inventoryLastQuery);
  const q = useQuery(inventoriesQuery());
  const canStart = (warehouses.data ?? []).some((w) => w.canOperate && w.kind !== 'transit');
  const [starting, setStarting] = useState(false);
  const overdue = (last.data ?? []).filter((x) => x.overdue);
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[14px] text-ink-2">
          Снимок учёта на момент начала, пересчёт с телефона по QR, ведомость расхождений утверждает
          руководитель проекта; плановая — раз в квартал и при смене МОЛ (СК-26)
        </p>
        {canStart ? (
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setStarting(true)}>
            Начать инвентаризацию
          </Button>
        ) : null}
      </div>
      {overdue.length ? (
        <div className="mb-4 rounded-md bg-warning-soft px-4 py-3 text-[13px]">
          <p className="font-medium">Плановая инвентаризация просрочена (больше квартала или ещё не было):</p>
          <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {overdue.map((x) => {
              const w = warehouses.data?.find((y) => y.id === x.warehouseId);
              return (
                <li key={x.warehouseId}>
                  <Link to={`/warehouse/warehouses/${x.warehouseId}`} className="hover:underline">
                    {w?.name ?? '—'}
                  </Link>
                  <span className="text-ink-2">
                    {' '}
                    — {x.lastApprovedAt ? `последняя ${date(x.lastApprovedAt)}` : 'не проводилась'}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      <Panel bodyClassName="p-0">
        {q.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : q.isError ? (
          <ErrorBox error={q.error} className="m-5" />
        ) : q.data.length ? (
          <ul className="divide-y divide-line">
            {q.data.map((inv) => (
              <li
                key={inv.id}
                className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 px-5 py-3"
              >
                <div className="min-w-0">
                  <Link
                    to={`/warehouse/inventories/${inv.id}`}
                    className="text-[15px] font-medium text-ink hover:underline"
                  >
                    {inv.number}
                  </Link>
                  <span className="ml-2 text-[14px]">{inv.warehouseName}</span>
                  <div className="text-[13px] text-ink-2">
                    {INVENTORY_REASONS[inv.reason]} · начата {dateTime(inv.startedAt)}
                    {inv.approvedAt
                      ? ` · утверждена ${dateTime(inv.approvedAt)}, ${inv.approvedByName ?? '—'}`
                      : ''}
                  </div>
                  <div className="num text-[12px] text-ink-2">
                    позиций {inv.lines}, пересчитано {inv.counted}, с расхождением {inv.withDiff}
                    {inv.documentNumber ? ` · корректировки ${inv.documentNumber}` : ''}
                  </div>
                </div>
                <Badge tone={INVENTORY_TONE[inv.status]}>{INVENTORY_STATUSES[inv.status]}</Badge>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-5">
            <EmptyState title="Инвентаризаций ещё не было">
              Начните с центрального склада: снимок учёта делается в момент начала, склад продолжает работать.
            </EmptyState>
          </div>
        )}
      </Panel>
      {starting ? (
        <StartInventoryDialog
          onClose={() => setStarting(false)}
          onDone={(d) => navigate({ to: `/warehouse/inventories/${d.id}` })}
        />
      ) : null}
    </>
  );
}

export function StartInventoryDialog({
  presetWarehouseId,
  onClose,
  onDone,
}: {
  presetWarehouseId?: string;
  onClose: () => void;
  onDone: (d: InventoryDetail) => void;
}) {
  const warehouses = useQuery(warehousesQuery);
  const [f, setF] = useState({
    warehouseId: presetWarehouseId ?? '',
    reason: 'planned' as InventoryReason,
    note: '',
  });
  const m = useMutation({
    mutationFn: () =>
      api<InventoryDetail>('/warehouse/inventories', {
        body: { warehouseId: f.warehouseId, reason: f.reason, note: f.note.trim() || null },
      }),
    onSuccess: (d) => {
      toast(`Инвентаризация ${d.number} начата — снимок учёта сделан`);
      onClose();
      onDone(d);
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Начать инвентаризацию"
      description="Снимок учёта делается сейчас; ожидаемое при вводе = снимок + движения после начала"
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={!f.warehouseId}
            loading={m.isPending}
            onClick={() => m.mutate()}
          >
            Начать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Склад">
          <Select value={f.warehouseId} onChange={(e) => setF({ ...f, warehouseId: e.target.value })}>
            <option value="">Выберите</option>
            {(warehouses.data ?? [])
              .filter((w) => w.canOperate && w.kind !== 'transit')
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Причина">
          <Select
            value={f.reason}
            onChange={(e) => setF({ ...f, reason: e.target.value as InventoryReason })}
          >
            {(Object.keys(INVENTORY_REASONS) as InventoryReason[]).map((k) => (
              <option key={k} value={k}>
                {INVENTORY_REASONS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Примечание">
          <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/* ───────────────────────── Инструмент ───────────────────────── */

export function ToolsTab() {
  const navigate = useNavigate();
  const me = useMe();
  const warehouses = useQuery(warehousesQuery);
  const canCreate = (warehouses.data ?? []).some((w) => w.canOperate && w.kind !== 'transit');
  const [q, setQ] = useState('');
  const [flag, setFlag] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const list = useQuery(
    toolsQuery({
      q: q.trim().length >= 2 ? q.trim() : undefined,
      flag: flag || undefined,
      warehouseId: warehouseId || undefined,
    }),
  );
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const pending = (list.data ?? []).filter((u) => u.pendingHolderUserId === me.user.id);
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          type="search"
          className="h-9 max-w-[280px]"
          placeholder="Инвентарный, серийный номер, модель"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select className="h-9 w-auto" value={flag} onChange={(e) => setFlag(e.target.value)}>
          <option value="">Все</option>
          <option value="on_hands">На руках</option>
          <option value="mine">У меня</option>
          <option value="pending">Ждут подтверждения</option>
          <option value="overdue">Возврат просрочен</option>
          <option value="check_soon">Проверка скоро или просрочена</option>
          <option value="check_overdue">Выдача закрыта: срок проверки прошёл</option>
        </Select>
        <Select
          className="h-9 w-auto max-w-full"
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
        >
          <option value="">Все склады</option>
          {(warehouses.data ?? []).map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </Select>
        <div className="ml-auto flex flex-wrap gap-2">
          {selected.length ? (
            <Button
              icon={<QrCode className="size-4" />}
              onClick={() => navigate({ to: '/warehouse/labels', search: { units: selected.join(',') } })}
            >
              Этикетки: {selected.length}
            </Button>
          ) : null}
          {canCreate ? (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Новый экземпляр
            </Button>
          ) : null}
        </div>
      </div>
      {pending.length ? (
        <div className="mb-4 rounded-md bg-info-soft px-4 py-3 text-[13px]">
          <p className="font-medium">
            Вам выдан инструмент — подтвердите получение, только тогда он числится за вами (СК-29):
          </p>
          <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {pending.map((u) => (
              <li key={u.id}>
                <Link to={`/warehouse/tools/${u.id}`} className="hover:underline">
                  {u.inventoryNumber} · {u.itemName}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <Panel bodyClassName="p-0">
        {list.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : list.isError ? (
          <ErrorBox error={list.error} className="m-5" />
        ) : list.data.length ? (
          <>
            <ul className="divide-y divide-line md:hidden">
              {list.data.map((u) => (
                <li key={u.id} className="flex items-start gap-2 py-2 pr-4 pl-2 text-[13px]">
                  <label className="flex size-11 shrink-0 cursor-pointer items-center justify-center">
                    <input
                      type="checkbox"
                      aria-label={`Выбрать ${u.inventoryNumber}`}
                      className="size-4"
                      checked={selected.includes(u.id)}
                      onChange={() => toggle(u.id)}
                    />
                  </label>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-1.5">
                    <Link
                      to={`/warehouse/tools/${u.id}`}
                      className="text-[14px] font-medium text-ink hover:underline"
                    >
                      {u.inventoryNumber} · {u.itemName}
                    </Link>
                    <span className="text-ink-2">
                      {u.counterpartyName
                        ? `у подрядчика ${u.counterpartyName}`
                        : u.holderName
                          ? `у ${u.holderName}`
                          : u.pendingHolderName
                            ? `ждёт подтверждения ${u.pendingHolderName}`
                            : u.warehouseName}
                    </span>
                    <span className="flex flex-wrap gap-1">
                      <Badge tone={TOOL_STATE_TONE[u.state]}>{TOOL_STATES[u.state]}</Badge>
                      {u.checkKind !== 'none' ? (
                        <Badge tone={CHECK_TONE[u.checkState]}>
                          проверка {u.nextCheckOn ? date(u.nextCheckOn) : '—'}
                        </Badge>
                      ) : null}
                      {u.returnOverdue ? (
                        <Badge tone="danger">возврат просрочен</Badge>
                      ) : u.returnBy ? (
                        <Badge tone="neutral">вернуть до {date(u.returnBy)}</Badge>
                      ) : null}
                      {u.preservationState !== 'none' ? (
                        <Badge tone={CHECK_TONE[u.preservationState]}>
                          консервация {u.preservationDueOn ? date(u.preservationDueOn) : '—'}
                        </Badge>
                      ) : null}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            <ScrollX className="hidden md:block">
              <table className="w-full min-w-[860px] text-[14px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="w-10 py-2.5 pl-4">
                      <span className="sr-only">Выбрать</span>
                    </th>
                    <th className="py-2.5 pr-3 font-medium">Экземпляр</th>
                    <th className="py-2.5 pr-3 font-medium">Где / у кого</th>
                    <th className="py-2.5 pr-3 font-medium">Вернуть до</th>
                    <th className="py-2.5 pr-3 font-medium">Проверка</th>
                    <th className="py-2.5 pr-5 font-medium">Состояние</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((u) => (
                    <tr key={u.id} className="border-t border-line align-top">
                      <td className="pl-2">
                        <label className="flex size-11 cursor-pointer items-center justify-center">
                          <input
                            type="checkbox"
                            aria-label={`Выбрать ${u.inventoryNumber}`}
                            className="size-4"
                            checked={selected.includes(u.id)}
                            onChange={() => toggle(u.id)}
                          />
                        </label>
                      </td>
                      <td className="py-2.5 pr-3">
                        <Link
                          to={`/warehouse/tools/${u.id}`}
                          className="font-medium text-ink hover:underline"
                        >
                          {u.inventoryNumber}
                        </Link>
                        <div className="text-[12px] text-ink-2">
                          {u.itemName}
                          {u.model ? ` · ${u.model}` : ''}
                        </div>
                      </td>
                      <td className="py-2.5 pr-3">
                        {u.counterpartyName ? (
                          `подрядчик ${u.counterpartyName}`
                        ) : u.holderName ? (
                          u.holderName
                        ) : u.pendingHolderName ? (
                          <span className="text-ink-2">ждёт подтверждения: {u.pendingHolderName}</span>
                        ) : (
                          <span className="text-ink-2">{u.warehouseName}</span>
                        )}
                      </td>
                      <td className="num py-2.5 pr-3">
                        {u.returnBy ? date(u.returnBy) : '—'}
                        {u.returnOverdue ? <div className="text-[12px] text-danger">просрочен</div> : null}
                      </td>
                      <td className="py-2.5 pr-3">
                        {u.checkKind === 'none' ? (
                          <span className="text-ink-2">—</span>
                        ) : (
                          <Badge tone={CHECK_TONE[u.checkState]}>
                            {u.nextCheckOn ? date(u.nextCheckOn) : 'не было'}
                          </Badge>
                        )}
                      </td>
                      <td className="py-2.5 pr-5">
                        <Badge tone={TOOL_STATE_TONE[u.state]}>{TOOL_STATES[u.state]}</Badge>
                        {u.preservationState === 'overdue' || u.preservationState === 'soon' ? (
                          <Badge tone={CHECK_TONE[u.preservationState]} className="ml-1">
                            консервация {u.preservationDueOn ? date(u.preservationDueOn) : ''}
                          </Badge>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          </>
        ) : (
          <div className="p-5">
            <EmptyState title="Экземпляров нет">
              Инструмент, средства измерений, такелаж и оборудование учитываются поштучно: инвентарный номер,
              QR, проверки (СК-28).
            </EmptyState>
          </div>
        )}
      </Panel>
      {creating ? (
        <Dialog open onOpenChange={(v) => !v && setCreating(false)} title="Новый экземпляр" width="lg">
          <ToolUnitForm
            onCancel={() => setCreating(false)}
            onSaved={(u) => navigate({ to: `/warehouse/tools/${u.id}` })}
          />
        </Dialog>
      ) : null}
    </>
  );
}
